/**
 * Cuándo se MUESTRA cada error. Las reglas de qué está mal viven en `validacion.js`.
 *
 * Son dos preguntas distintas y se separan a propósito: la regla no cambia nunca, pero
 * el momento de enseñarla decide si el formulario ayuda o regaña. El criterio que se
 * implementa aquí es el estándar de usabilidad de formularios largos
 * ("reward early, punish late"), y en la práctica son tres reglas:
 *
 *   1. **Mientras se escribe por primera vez, no se dice nada.** Marcar en rojo un campo
 *      medio tecleado ("mtorres@" todavía no es un correo) es acusar al usuario de un
 *      error que aún no ha cometido.
 *   2. **Al SALIR del control se valida ese campo** (`onBlur`), y solo ese. Es el momento
 *      en que el usuario ya dio el dato por terminado.
 *   3. **Una vez que un campo mostró error, se revalida en cada tecla.** El error
 *      desaparece en cuanto se corrige, sin esperar a salir otra vez: castigar tarde,
 *      premiar pronto. Sale gratis porque las reglas se recalculan con el caso.
 *
 * Al pulsar «Enviar» se marcan TODOS como tocados (`tocarTodo`): ahí sí hay que enseñar
 * de golpe todo lo que falta, que es lo que HU-01 pide.
 *
 * Nada de esto bloquea el guardado: el borrador se persiste incompleto (HU-36).
 */
import { useCallback, useMemo, useState } from 'react';

import { evaluarFormulario } from './validacion';
import type { CasoDelFormulario, ConfiguracionDeCorreos, Faltante } from '../tipos';

export function useValidacionCampos(
  caso: CasoDelFormulario,
  afectaPersonas: boolean,
  configuracion: ConfiguracionDeCorreos = {},
) {
  // `null` = aún no se ha intentado enviar. Al enviar pasa a `true` y todos los campos
  // se consideran tocados, tengan o no foco previo.
  const [todos, setTodos] = useState(false);
  const [tocados, setTocados] = useState(() => new Set<string>());

  // `configuracion` son las listas de dominios de HU-31. Se desestructura en las dos
  // referencias que de verdad importan para que un objeto nuevo en cada render —que es
  // lo que devuelve el contexto— no vuelva a evaluar el formulario entero cada vez.
  const desechables = configuracion.desechables;
  const frecuentes = configuracion.frecuentes;
  const entradas = useMemo(
    () => evaluarFormulario(caso, afectaPersonas, { desechables, frecuentes }),
    [caso, afectaPersonas, desechables, frecuentes],
  );

  // Índice campo → primer mensaje. Un campo puede infringir dos reglas a la vez (vacío y
  // con formato malo no ocurre, pero sí "cuenta incoherente" sobre un número): junto al
  // control cabe uno, y el resumen los lista todos.
  const porCampo = useMemo(() => {
    const m = new Map<string, string>();
    for (const x of entradas) if (!m.has(x.campo)) m.set(x.campo, x.mensaje ?? x.detalle ?? '');
    return m;
  }, [entradas]);

  // Los faltantes agrupados por sección, en el orden en que se evalúan.
  const porSeccion = useMemo(() => {
    const m: Record<string, Faltante[]> = {};
    // La sección se crea la primera vez que aparece un faltante suyo, en dos pasos:
    // asignar dentro de la expresión que además se usa esconde el efecto (S1121).
    for (const x of entradas) {
      m[x.seccion] ??= [];
      m[x.seccion].push(x);
    }
    return m;
  }, [entradas]);

  const tocar = useCallback((campo: string) => {
    setTocados((prev) => (prev.has(campo) ? prev : new Set(prev).add(campo)));
  }, []);

  const tocarTodo = useCallback(() => setTodos(true), []);

  const visible = useCallback(
    (campo: string) => todos || tocados.has(campo),
    [todos, tocados],
  );

  // El mensaje que toca pintar junto al control, o `undefined` si todavía no toca.
  const errorDe = useCallback(
    (campo: string) => (visible(campo) ? porCampo.get(campo) : undefined),
    [visible, porCampo],
  );

  return {
    entradas,
    porSeccion,
    // Cuántos DATOS faltan, no cuántas secciones (HU-01).
    cuantosFaltan: entradas.length,
    completo: entradas.length === 0,
    intentoEnvio: todos,
    errorDe,
    tocar,
    tocarTodo,
    visible,
  };
}
